import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Search, ChevronRight, HelpCircle, User, Building, 
  CreditCard, Shield, MessageSquare, Calendar, Star,
  ChevronDown, ChevronUp
} from 'lucide-react';

const faqCategories = [
  { id: 'getting-started', label: 'Getting Started', icon: HelpCircle },
  { id: 'guests', label: 'For Guests', icon: User },
  { id: 'hosts', label: 'For Hosts', icon: Building },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'safety', label: 'Safety & Trust', icon: Shield },
];

const faqs = {
  'getting-started': [
    {
      q: 'What is Barns & Pastures?',
      a: 'Barns & Pastures is a marketplace connecting horse and livestock owners with trusted hosts who have available barn stalls, pastures, and boarding facilities. Think of it as Airbnb for your animals.',
    },
    {
      q: 'How do I create an account?',
      a: 'Click "Sign In" in the top navigation, then select the "Sign Up" tab. Choose whether you want to be a Guest (looking for boarding) or a Host (offering boarding), fill in your details, and you\'re ready to go.',
    },
    {
      q: 'Is Barns & Pastures available in my area?',
      a: 'We currently operate across all 50 US states. Use our search feature to find listings near your location. We\'re constantly adding new hosts to expand coverage.',
    },
    {
      q: 'What types of animals are supported?',
      a: 'While we primarily cater to horses, many hosts also accept cattle, sheep, goats, donkeys, llamas, and other livestock. Each listing specifies which species are welcome.',
    },
  ],
  'guests': [
    {
      q: 'How do I book a listing?',
      a: 'Search for listings in your desired location, select dates and number of animals, then click "Request to Book." Add a message introducing yourself and your animals. The host will review and accept or decline your request.',
    },
    {
      q: 'What is Request to Book?',
      a: 'Unlike instant booking, Request to Book allows hosts to review each booking request before accepting. This ensures hosts can verify they can accommodate your specific needs.',
    },
    {
      q: 'Can I message a host before booking?',
      a: 'Yes! We encourage guests to message hosts with any questions before submitting a booking request. This helps ensure the listing is a good fit for your animals.',
    },
    {
      q: 'What if I need to cancel my booking?',
      a: 'You can cancel your booking from your Trips page. Cancellation policies vary by listing—check the listing details for specific terms. We recommend contacting the host first to discuss any changes.',
    },
    {
      q: 'How do reviews work?',
      a: 'After a completed stay, both guests and hosts can leave reviews. Reviews include a 1-5 star rating and written feedback. This helps build trust in our community.',
    },
  ],
  'hosts': [
    {
      q: 'How do I become a host?',
      a: 'Create an account and select "Host" as your role. Then click "New Listing" from your dashboard to add your first property. Our team will review and approve listings that meet our quality standards.',
    },
    {
      q: 'How do I set my pricing?',
      a: 'When creating a listing, you can set nightly, weekly, and monthly rates. Consider your local market, amenities offered, and the level of care provided when setting prices.',
    },
    {
      q: 'Can I block dates on my calendar?',
      a: 'Yes! Use the availability calendar in your listing management to block dates when your facility isn\'t available. This prevents booking requests for those periods.',
    },
    {
      q: 'How long do I have to respond to booking requests?',
      a: 'We recommend responding within 24 hours. Quick response times improve your listing\'s visibility and help guests plan their trips.',
    },
    {
      q: 'What if I need to cancel a booking?',
      a: 'While we discourage host cancellations, we understand emergencies happen. Contact the guest directly and then cancel through your dashboard. Frequent cancellations may affect your listing\'s standing.',
    },
  ],
  'payments': [
    {
      q: 'How do payments work?',
      a: 'When a host accepts a booking, the guest\'s payment is authorized. Funds are held securely and released to the host after check-in (or according to the payout schedule).',
    },
    {
      q: 'When do hosts get paid?',
      a: 'Hosts receive payouts within 24-48 hours after the guest\'s check-in date, minus the platform service fee. Payouts are sent directly to your connected bank account.',
    },
    {
      q: 'What payment methods are accepted?',
      a: 'We accept all major credit cards and debit cards. Guests can securely save payment methods for faster booking.',
    },
    {
      q: 'Are there any fees?',
      a: 'Guests pay a small service fee added to the booking total. Hosts pay a percentage of the booking amount as a service fee. All fees are clearly displayed before confirming.',
    },
    {
      q: 'What happens if there\'s a payment dispute?',
      a: 'If there\'s an issue with a booking, you can open a dispute through our platform. Our team will review the case and help mediate a fair resolution.',
    },
  ],
  'safety': [
    {
      q: 'How are hosts verified?',
      a: 'All listings go through a review process before being approved. We verify host identity, review listing details and photos, and may conduct additional checks for premium listings.',
    },
    {
      q: 'What if something goes wrong during a stay?',
      a: 'Contact your host immediately to address any issues. If you can\'t resolve it directly, open a dispute through your booking details. Our support team will help mediate.',
    },
    {
      q: 'Is my personal information secure?',
      a: 'We use industry-standard encryption to protect your data. We never share your personal contact information with other users until a booking is confirmed.',
    },
    {
      q: 'What vaccination requirements are there?',
      a: 'Requirements vary by listing—hosts can specify if vaccination records are required. Always check the listing\'s rules section and communicate any questions with the host.',
    },
    {
      q: 'How do I report a problem?',
      a: 'Use the "Report Issue" button on any booking or listing, or contact our support team directly. We take all reports seriously and investigate thoroughly.',
    },
  ],
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-farmhouse-subtle last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left"
      >
        <span className="font-medium text-farmhouse-cream pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-farmhouse-muted shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-farmhouse-muted shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-farmhouse-muted leading-relaxed animate-fade-in">
          {answer}
        </div>
      )}
    </div>
  );
};

export default function HelpCenter() {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFaqs = searchQuery
    ? Object.entries(faqs).flatMap(([category, items]) =>
        items.filter(
          (faq) =>
            faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.a.toLowerCase().includes(searchQuery.toLowerCase())
        ).map(faq => ({ ...faq, category }))
      )
    : faqs[activeCategory];

  return (
    <div className="min-h-screen bg-farmhouse-charcoal" data-testid="help-center-page">
      {/* Hero */}
      <section className="pt-16 pb-12 px-4 bg-farmhouse-paper">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-farmhouse-saddle tracking-[0.2em] uppercase text-sm font-medium mb-4">
            Help Center
          </p>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-farmhouse-cream mb-6">
            How Can We Help?
          </h1>
          <p className="text-xl text-farmhouse-muted mb-8">
            Find answers to common questions or contact our support team.
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-farmhouse-muted" />
            <Input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input pl-12 h-14 text-lg"
              data-testid="help-search"
            />
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 bg-farmhouse-charcoal border-b border-farmhouse-subtle">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/how-it-works" className="flex items-center gap-2 px-4 py-2 bg-farmhouse-paper rounded-md text-farmhouse-cream hover:bg-farmhouse-subtle transition-colors">
              <HelpCircle className="w-4 h-4" />
              How It Works
            </Link>
            <Link to="/contact" className="flex items-center gap-2 px-4 py-2 bg-farmhouse-paper rounded-md text-farmhouse-cream hover:bg-farmhouse-subtle transition-colors">
              <MessageSquare className="w-4 h-4" />
              Contact Support
            </Link>
            <Link to="/search" className="flex items-center gap-2 px-4 py-2 bg-farmhouse-paper rounded-md text-farmhouse-cream hover:bg-farmhouse-subtle transition-colors">
              <Search className="w-4 h-4" />
              Browse Listings
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Categories Sidebar */}
            {!searchQuery && (
              <div className="md:col-span-1">
                <h3 className="font-serif text-lg font-semibold text-farmhouse-cream mb-4">
                  Categories
                </h3>
                <nav className="space-y-1">
                  {faqCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors text-left ${
                        activeCategory === category.id
                          ? 'bg-farmhouse-saddle/10 text-farmhouse-saddle'
                          : 'text-farmhouse-muted hover:bg-farmhouse-paper hover:text-farmhouse-cream'
                      }`}
                    >
                      <category.icon className="w-5 h-5" />
                      <span>{category.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            )}

            {/* FAQ Content */}
            <div className={searchQuery ? 'md:col-span-4' : 'md:col-span-3'}>
              {searchQuery ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream">
                      Search Results
                    </h2>
                    <Button
                      variant="ghost"
                      onClick={() => setSearchQuery('')}
                      className="text-farmhouse-muted hover:text-farmhouse-cream"
                    >
                      Clear Search
                    </Button>
                  </div>
                  {filteredFaqs.length > 0 ? (
                    <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
                      {filteredFaqs.map((faq, index) => (
                        <div key={index}>
                          <Badge className="mb-2 bg-farmhouse-subtle text-farmhouse-muted border-0 capitalize">
                            {faq.category.replace('-', ' ')}
                          </Badge>
                          <FAQItem question={faq.q} answer={faq.a} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-farmhouse-paper border border-farmhouse-subtle rounded-md">
                      <Search className="w-12 h-12 mx-auto mb-4 text-farmhouse-subtle" />
                      <p className="text-farmhouse-muted">No results found for "{searchQuery}"</p>
                      <p className="text-farmhouse-muted text-sm mt-2">
                        Try different keywords or <Link to="/contact" className="text-farmhouse-saddle hover:underline">contact support</Link>
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream mb-6">
                    {faqCategories.find(c => c.id === activeCategory)?.label}
                  </h2>
                  <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
                    {faqs[activeCategory].map((faq, index) => (
                      <FAQItem key={index} question={faq.q} answer={faq.a} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-farmhouse-paper">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-semibold text-farmhouse-cream mb-4">
            Still Have Questions?
          </h2>
          <p className="text-farmhouse-muted mb-6">
            Our support team is here to help you with any questions or concerns.
          </p>
          <Link to="/contact">
            <Button className="btn-primary">
              Contact Support
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
